import { getDeclension, getPlural, getPluralGenitive } from '@/utils/morphology'
import logger from '@/utils/logger'
import { getI18n, translateDbValue } from '@/utils/dbI18n'

/**
 * Термин рабочего места («Пост» / «Рабочее место» / «Ажлын байр»).
 *
 * Источник термина — настройка организации `service_place`:
 * - ключ перевода (`interface.workplace.terms.*`) — пресет, формы берём из локалей;
 * - любой другой текст — название, введённое пользователем (динамический текст).
 *
 * Формы пресета лежат в `/locales` (`interface.workplace.terms.<term>.<форма>`),
 * поэтому новый язык добавляется правками локалей, а не кода.
 */

const TERM_PREFIX = 'interface.workplace.terms.'

/** Ключ со словами-местами («кабинет», «зал», «клиника»…) для подписи места. */
const PLACE_WORD_KEY = 'interface.workplace.place_words'

/** Ключи пресетов термина: универсальный ЛК и автомобильный бизнес. */
export const WORKPLACE_TERM_KEYS = Object.freeze({
  PLACE: `${TERM_PREFIX}place`,
  POST: `${TERM_PREFIX}post`,
})

/** Ключ локализованных слов-мест. */
export const WORKPLACE_PLACE_WORD_KEY = PLACE_WORD_KEY

/** Формы термина, которые поддерживают локали. */
export const WORKPLACE_FORMS = Object.freeze([
  'nominative',
  'genitive',
  'dative',
  'accusative',
  'instrumental',
  'prepositional',
  'plural',
  'pluralGenitive',
  'pluralDative',
  'pluralAccusative',
  'pluralAblative',
  'pluralInstrumental',
])

function lowerFirst(text) {
  return text && text.length > 0
    ? text.charAt(0).toLowerCase() + text.slice(1)
    : text
}

/** Значение из БД — ключ пресета термина. */
export function isWorkplaceTerm(value) {
  return typeof value === 'string' && value.startsWith(TERM_PREFIX)
}

/** Формы пользовательского названия: русская морфология, для остальных языков — как есть. */
function getCustomNameForm(value, form, { capitalize, i18n }) {
  const locale = i18n?.locale || 'ru'

  if (locale !== 'ru') {
    return capitalize ? value : lowerFirst(value)
  }

  let declined = null
  if (form === 'plural') {
    declined = getPlural(value)
  } else if (form === 'pluralGenitive') {
    declined = getPluralGenitive(value)
  } else if (
    form === 'pluralAccusative' ||
    form === 'pluralAblative' ||
    form === 'pluralDative' ||
    form === 'pluralInstrumental'
  ) {
    // Отдельных форм множественного числа в словаре нет — берём множественное число.
    declined = getPlural(value)
  } else {
    declined = getDeclension(value, form, capitalize)
  }

  if (!declined) return capitalize ? value : lowerFirst(value)

  return capitalize ? declined : lowerFirst(declined)
}

/**
 * Форма термина на текущем языке интерфейса.
 * @param {string} value - значение `service_place` из БД (ключ пресета или название)
 * @param {string} [form='nominative']
 * @param {{ capitalize?: boolean, i18n?: Object }} [options]
 * @returns {string}
 */
export function getWorkplaceForm(
  value,
  form = 'nominative',
  { capitalize = true, i18n = getI18n() } = {}
) {
  if (!value) return ''

  if (isWorkplaceTerm(value)) {
    const text = translateDbValue(value, { form, i18n })

    if (text === value) {
      logger.error({ value, form }, 'Нет перевода формы термина рабочего места')

      return ''
    }

    return capitalize ? text : lowerFirst(text)
  }

  return getCustomNameForm(value, form, { capitalize, i18n })
}

/**
 * Подпись места для тоста и подобных фраз: «Пост 5» или «Пост Приёмка».
 *
 * Если название уже начинается со слова-места («Пост 5», «Кабинет 3», «Зал А»),
 * отдаём его как есть — иначе получилось бы «Пост Кабинет 3». Если название
 * произвольное («Приёмка») — добавляем термин из локали.
 *
 * Раньше список слов-мест был зашит в регулярку на русском, из-за чего на
 * монгольском инстансе («Ажлын байр 1») добавлялся второй термин:
 * «Ажлын байр Ажлын байр 1». Теперь слова-места лежат в локалях
 * (`interface.workplace.place_words`), а формы термина берутся из
 * `interface.workplace.terms.*` — новый язык добавляется словарём, не кодом.
 *
 * @param {string} name - название места из данных
 * @param {{ term?: string, i18n?: Object }} [options]
 *   term — значение `service_place` (ключ пресета или пользовательское название)
 * @returns {string}
 */
export function getPlaceLabel(name, { term, i18n = getI18n() } = {}) {
  const value = String(name || '').trim()

  if (!value) return ''

  const termKey = term || WORKPLACE_TERM_KEYS.POST
  const words = getPlaceWords([termKey, WORKPLACE_TERM_KEYS.PLACE], i18n)

  if (startsWithAnyWord(value, words)) return value

  /* Именительный, а не винительный: подпись места — это название
     («Пост Приёмка»), а не объект действия. В русском формы совпадают,
     в монгольском отличаются: «Ажлын байр» против «Ажлын байрыг». */
  const termForm = getWorkplaceForm(termKey, 'nominative', {
    capitalize: false,
    i18n,
  })

  return `${termForm} ${value}`.trim()
}

/** Слова-места: список из локали. */
function getPlaceWords(termKeys, i18n) {
  const words = []

  /* Формы термина: и пресет (`interface.workplace.terms.*`), и пользовательское
     название — у второго форм в локали нет, поэтому берём как есть. */
  for (const key of termKeys) {
    if (isWorkplaceTerm(key)) {
      for (const form of WORKPLACE_FORMS) {
        const text = translateDbValue(key, { form, i18n })

        if (text && text !== key) words.push(text)
      }
    } else if (key) {
      words.push(key)
    }
  }

  const localized = i18n?.te(PLACE_WORD_KEY) ? i18n.t(PLACE_WORD_KEY) : null

  if (localized) {
    words.push(
      ...String(localized)
        .split(',')
        .map((word) => word.trim())
        .filter(Boolean)
    )
  }

  return words
}

/** Название начинается с одного из слов (без учёта регистра). */
function startsWithAnyWord(value, words) {
  const lower = value.toLowerCase()

  return words.some((word) => lower.startsWith(String(word).toLowerCase()))
}

/** Авто → пост; универсальный → ввод пользователя или «место». */
export function resolveWorkplaceTerm(isUniversal, customPlace) {
  if (!isUniversal) return WORKPLACE_TERM_KEYS.POST

  return customPlace?.trim() || WORKPLACE_TERM_KEYS.PLACE
}

export default {
  getWorkplaceForm,
  getPlaceLabel,
  isWorkplaceTerm,
  resolveWorkplaceTerm,
  WORKPLACE_TERM_KEYS,
  WORKPLACE_FORMS,
  WORKPLACE_PLACE_WORD_KEY,
}
